import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertCircle, Building2, CalendarDays, CheckCircle2, Clock3, FileAudio, Tag, UploadCloud } from 'lucide-react';
import type { Business } from '@sbclite/core';
import {
  useAssignPromptToPolicy,
  useAnnouncementPrompts,
  useBusinessCustomers,
  useBusinessCallHandlingPolicies,
  useCreateBusinessCallHandlingPolicy,
  useDisableBusinessCallHandlingOverride,
  useEnableBusinessCallHandlingOverride,
  usePromptAssignments,
  useUpdateBusinessCallHandlingPolicy,
  useUploadAnnouncementPrompt,
} from '../../hooks/useBusinessCallHandling';
import { getApiBaseUrl } from '../../lib/runtime-config';
import { useSipPeers } from '../../hooks/useSipPeers';
import { usePeerGroups } from '../../hooks/usePeerGroups';

function apiAssetUrl(path: string): string {
  const baseUrl = getApiBaseUrl();
  return baseUrl === '' ? path : `${baseUrl}${path}`;
}

function formatDuration(durationMs: number | null): string {
  if (durationMs === null) return 'Unknown';
  if (durationMs < 1000) return `${durationMs} ms`;
  return `${(durationMs / 1000).toFixed(1)} s`;
}

function statusClass(status: Business.AnnouncementPromptInfo['status']): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
    case 'rejected':
      return 'bg-rose-50 text-rose-700 ring-rose-600/20';
    case 'archived':
      return 'bg-slate-100 text-slate-600 ring-slate-500/20';
    case 'validating':
      return 'bg-amber-50 text-amber-700 ring-amber-600/20';
    case 'draft':
      return 'bg-blue-50 text-blue-700 ring-blue-600/20';
  }
}

type BusinessDay = Business.BusinessCallHandlingScheduleInfo['weeklyOpenHours'][number]['days'][number];
type AssignmentType = Business.BusinessCallHandlingPromptAssignmentInfo['assignmentType'];

interface WeeklyWindowDraft {
  readonly id: string;
  readonly days: BusinessDay[];
  readonly from: string;
  readonly to: string;
}

interface ExceptionDraft {
  readonly id: string;
  readonly date: string;
  readonly closed: boolean;
  readonly from: string;
  readonly to: string;
  readonly promptId: string;
  readonly reason: string;
}

const BUSINESS_DAYS: ReadonlyArray<{ readonly value: BusinessDay; readonly label: string }> = [
  { value: 'MO', label: 'Mon' },
  { value: 'TU', label: 'Tue' },
  { value: 'WE', label: 'Wed' },
  { value: 'TH', label: 'Thu' },
  { value: 'FR', label: 'Fri' },
  { value: 'SA', label: 'Sat' },
  { value: 'SU', label: 'Sun' },
];

const ASSIGNMENT_TYPES: ReadonlyArray<{ readonly value: AssignmentType; readonly label: string }> = [
  { value: 'closed_default', label: 'Closed default' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'exception', label: 'Exception' },
  { value: 'temporary_closure', label: 'Temporary closure' },
  { value: 'override', label: 'Override' },
];

export default function BusinessCallHandlingPage() {
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [displayName, setDisplayName] = useState('');
  const [codec, setCodec] = useState<Business.UploadedPromptCodec>('PCMA');
  const [policyName, setPolicyName] = useState('');
  const [editingPolicyId, setEditingPolicyId] = useState<string | undefined>();
  const [inboundResourceType, setInboundResourceType] = useState<'sip_peer' | 'peer_group' | 'number'>('sip_peer');
  const [inboundPeerId, setInboundPeerId] = useState<string | undefined>();
  const [inboundPeerGroupId, setInboundPeerGroupId] = useState<string | undefined>();
  const [inboundNumber, setInboundNumber] = useState('');
  const [openActionType, setOpenActionType] = useState<'route_peer' | 'route_peer_group' | 'route_registered_aor'>('route_peer');
  const [openPeerId, setOpenPeerId] = useState<string | undefined>();
  const [openPeerGroupId, setOpenPeerGroupId] = useState<string | undefined>();
  const [openAor, setOpenAor] = useState('');
  const [closedPromptId, setClosedPromptId] = useState<string | undefined>();
  const [weeklyWindows, setWeeklyWindows] = useState<WeeklyWindowDraft[]>([
    { id: 'weekday-default', days: ['MO', 'TU', 'WE', 'TH', 'FR'], from: '09:00', to: '17:00' },
  ]);
  const [exceptions, setExceptions] = useState<ExceptionDraft[]>([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | undefined>();
  const [assignmentType, setAssignmentType] = useState<AssignmentType>('holiday');
  const [assignmentKey, setAssignmentKey] = useState('');
  const [assignmentPromptId, setAssignmentPromptId] = useState<string | undefined>();
  const [overridePromptByPolicy, setOverridePromptByPolicy] = useState<Record<string, string>>({});
  const [overrideReasonByPolicy, setOverrideReasonByPolicy] = useState<Record<string, string>>({});

  const customersQuery = useBusinessCustomers();
  const promptsQuery = useAnnouncementPrompts(selectedCustomerId);
  const policiesQuery = useBusinessCallHandlingPolicies(selectedCustomerId);
  const assignmentsQuery = usePromptAssignments(selectedPolicyId);
  const peersQuery = useSipPeers({ enabled: true });
  const peerGroupsQuery = usePeerGroups();
  const uploadPrompt = useUploadAnnouncementPrompt();
  const createPolicy = useCreateBusinessCallHandlingPolicy();
  const updatePolicy = useUpdateBusinessCallHandlingPolicy();
  const assignPrompt = useAssignPromptToPolicy();
  const enableOverride = useEnableBusinessCallHandlingOverride();
  const disableOverride = useDisableBusinessCallHandlingOverride();
  const customers = customersQuery.data ?? [];
  const prompts = promptsQuery.data ?? [];
  const policies = policiesQuery.data ?? [];
  const peers = peersQuery.data?.data ?? [];
  const peerGroups = peerGroupsQuery.data?.data ?? [];
  const assignments = assignmentsQuery.data ?? [];
  const activePrompts = prompts.filter((prompt) => prompt.status === 'active');

  useEffect(() => {
    if (selectedCustomerId === undefined && customers[0] !== undefined) {
      setSelectedCustomerId(customers[0].id);
    }
  }, [customers, selectedCustomerId]);

  const selectedCustomer = customers.find((customer) => customer.id === selectedCustomerId);

  useEffect(() => {
    if (closedPromptId === undefined && activePrompts[0] !== undefined) setClosedPromptId(activePrompts[0].id);
    if (assignmentPromptId === undefined && activePrompts[0] !== undefined) setAssignmentPromptId(activePrompts[0].id);
  }, [activePrompts, assignmentPromptId, closedPromptId]);

  useEffect(() => {
    if (selectedPolicyId === undefined && policies[0] !== undefined) setSelectedPolicyId(policies[0].id);
  }, [policies, selectedPolicyId]);

  useEffect(() => {
    if (inboundPeerId === undefined && peers[0] !== undefined) setInboundPeerId(peers[0].id);
    if (openPeerId === undefined && peers[0] !== undefined) setOpenPeerId(peers[0].id);
  }, [inboundPeerId, openPeerId, peers]);

  useEffect(() => {
    if (inboundPeerGroupId === undefined && peerGroups[0] !== undefined) setInboundPeerGroupId(peerGroups[0].id);
    if (openPeerGroupId === undefined && peerGroups[0] !== undefined) setOpenPeerGroupId(peerGroups[0].id);
  }, [inboundPeerGroupId, openPeerGroupId, peerGroups]);

  const handleUpload = async () => {
    if (selectedCustomerId === undefined) {
      toast.error('Select a customer first');
      return;
    }
    if (selectedFile === undefined) {
      toast.error('Choose a raw G.711 prompt file');
      return;
    }

    try {
      await uploadPrompt.mutateAsync({
        customerId: selectedCustomerId,
        file: selectedFile,
        displayName,
        codec,
      });
      setSelectedFile(undefined);
      setDisplayName('');
      toast.success('Announcement prompt uploaded');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Prompt upload failed');
    }
  };

  const resetPolicyForm = () => {
    setEditingPolicyId(undefined);
    setPolicyName('');
    setInboundResourceType('sip_peer');
    setInboundPeerId(peers[0]?.id);
    setInboundPeerGroupId(peerGroups[0]?.id);
    setInboundNumber('');
    setOpenActionType('route_peer');
    setOpenPeerId(peers[0]?.id);
    setOpenPeerGroupId(peerGroups[0]?.id);
    setOpenAor('');
    setClosedPromptId(activePrompts[0]?.id);
    setWeeklyWindows([{ id: 'weekday-default', days: ['MO', 'TU', 'WE', 'TH', 'FR'], from: '09:00', to: '17:00' }]);
    setExceptions([]);
  };

  const loadPolicyIntoForm = (policy: Business.BusinessCallHandlingPolicyInfo) => {
    setEditingPolicyId(policy.id);
    setPolicyName(policy.name);
    setInboundResourceType(policy.inboundResourceType);
    setInboundPeerId(policy.inboundPeerId ?? undefined);
    setInboundPeerGroupId(policy.inboundPeerGroupId ?? undefined);
    setInboundNumber(policy.inboundNumber ?? '');
    setClosedPromptId(policy.closedAction.promptId);
    setWeeklyWindows(policy.schedule.weeklyOpenHours.map((window, index) => ({ id: `${policy.id}-window-${index}`, days: window.days, from: window.from, to: window.to })));
    setExceptions((policy.schedule.exceptions ?? []).map((exception, index) => ({
      id: `${policy.id}-exception-${index}`,
      date: exception.date,
      closed: exception.closed ?? false,
      from: exception.closed ? '09:00' : exception.from,
      to: exception.closed ? '17:00' : exception.to,
      promptId: exception.promptId ?? '',
      reason: exception.reason ?? '',
    })));
    if (policy.openAction.type === 'route_peer') {
      setOpenActionType('route_peer');
      setOpenPeerId(policy.openAction.peerId);
    } else if (policy.openAction.type === 'route_peer_group') {
      setOpenActionType('route_peer_group');
      setOpenPeerGroupId(policy.openAction.peerGroupId);
    } else {
      setOpenActionType('route_registered_aor');
      setOpenAor(policy.openAction.aor);
    }
  };

  const updateWeeklyWindow = (id: string, updates: Partial<Omit<WeeklyWindowDraft, 'id'>>) => {
    setWeeklyWindows((current) => current.map((window) => window.id === id ? { ...window, ...updates } : window));
  };

  const toggleWeeklyWindowDay = (id: string, day: BusinessDay) => {
    setWeeklyWindows((current) => current.map((window) => {
      if (window.id !== id) return window;
      const days = window.days.includes(day)
        ? window.days.filter((value) => value !== day)
        : [...window.days, day];
      return { ...window, days };
    }));
  };

  const updateException = (id: string, updates: Partial<Omit<ExceptionDraft, 'id'>>) => {
    setExceptions((current) => current.map((exception) => exception.id === id ? { ...exception, ...updates } : exception));
  };

  const handleSavePolicy = async () => {
    if (selectedCustomerId === undefined) {
      toast.error('Select a customer first');
      return;
    }
    if (policyName.trim().length === 0) {
      toast.error('Policy name is required');
      return;
    }
    if (closedPromptId === undefined) {
      toast.error('Upload or select a closed-hours prompt first');
      return;
    }
    const usableWindows = weeklyWindows.filter((window) => window.days.length > 0 && window.from.length > 0 && window.to.length > 0);
    if (usableWindows.length === 0) {
      toast.error('At least one open-hours window is required');
      return;
    }

    const openAction: Business.CreatePolicyInput['openAction'] = (() => {
      if (openActionType === 'route_peer') {
        if (openPeerId === undefined) throw new Error('Select an open-hours destination peer');
        return { type: 'route_peer', peerId: openPeerId };
      }
      if (openActionType === 'route_peer_group') {
        if (openPeerGroupId === undefined) throw new Error('Select an open-hours destination peer group');
        return { type: 'route_peer_group', peerGroupId: openPeerGroupId };
      }
      if (openAor.trim().length === 0) throw new Error('Registered AOR is required');
      return { type: 'route_registered_aor', aor: openAor.trim() };
    })();

    try {
      const input = {
        customerId: selectedCustomerId,
        name: policyName.trim(),
        description: null,
        status: 'active',
        enabled: true,
        inboundResourceType,
        inboundPeerId: inboundResourceType === 'sip_peer' ? inboundPeerId : null,
        inboundPeerGroupId: inboundResourceType === 'peer_group' ? inboundPeerGroupId : null,
        inboundNumber: inboundResourceType === 'number' ? inboundNumber.trim() : null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        schedule: {
          weeklyOpenHours: usableWindows.map((window) => ({ days: window.days, from: window.from, to: window.to })),
          exceptions: exceptions
            .filter((exception) => exception.date.length > 0)
            .map((exception) => exception.closed
              ? {
                  date: exception.date,
                  closed: true as const,
                  ...(exception.promptId.length === 0 ? {} : { promptId: exception.promptId }),
                  ...(exception.reason.trim().length === 0 ? {} : { reason: exception.reason.trim() }),
                }
              : {
                  date: exception.date,
                  closed: false as const,
                  from: exception.from,
                  to: exception.to,
                  ...(exception.promptId.length === 0 ? {} : { promptId: exception.promptId }),
                  ...(exception.reason.trim().length === 0 ? {} : { reason: exception.reason.trim() }),
                }),
        },
        openAction,
        closedAction: { type: 'announcement', promptId: closedPromptId },
        createdByUserId: null,
      } satisfies Business.CreatePolicyInput;

      if (editingPolicyId === undefined) {
        await createPolicy.mutateAsync(input);
        toast.success('Business call-handling policy created');
      } else {
        const updates: Business.UpdatePolicyInput['updates'] = {
          name: input.name,
          description: input.description,
          status: input.status,
          enabled: input.enabled,
          inboundResourceType: input.inboundResourceType,
          inboundPeerId: input.inboundPeerId,
          inboundPeerGroupId: input.inboundPeerGroupId,
          inboundNumber: input.inboundNumber,
          timezone: input.timezone,
          schedule: input.schedule,
          openAction: input.openAction,
          closedAction: input.closedAction,
        };
        await updatePolicy.mutateAsync({ id: editingPolicyId, updates });
        toast.success('Business call-handling policy updated');
      }
      resetPolicyForm();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Policy creation failed');
    }
  };

  const handleAssignPrompt = async () => {
    if (selectedPolicyId === undefined) {
      toast.error('Select a policy first');
      return;
    }
    if (assignmentPromptId === undefined) {
      toast.error('Select a prompt first');
      return;
    }

    try {
      await assignPrompt.mutateAsync({
        policyId: selectedPolicyId,
        assignmentType,
        assignmentKey: assignmentKey.trim().length === 0 ? null : assignmentKey.trim(),
        promptId: assignmentPromptId,
      });
      setAssignmentKey('');
      toast.success('Prompt assignment saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to assign prompt');
    }
  };

  const handleEnableOverride = async (policyId: string) => {
    const promptId = overridePromptByPolicy[policyId] ?? activePrompts[0]?.id;
    if (promptId === undefined || promptId.length === 0) {
      toast.error('Select an override prompt');
      return;
    }
    try {
      await enableOverride.mutateAsync({
        policyId,
        promptId,
        reason: overrideReasonByPolicy[policyId]?.trim() || 'Manual override from console',
      });
      toast.success('Manual override enabled');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to enable override');
    }
  };

  const handleDisableOverride = async (policyId: string) => {
    try {
      await disableOverride.mutateAsync(policyId);
      toast.success('Manual override disabled');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to disable override');
    }
  };

  return (
    <div className="space-y-8">
      <div className="overflow-hidden rounded-3xl bg-slate-950 shadow-2xl ring-1 ring-slate-900/10">
        <div className="relative px-8 py-9 sm:px-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(14,165,233,0.28),_transparent_32%),radial-gradient(circle_at_bottom_left,_rgba(16,185,129,0.18),_transparent_30%)]" />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
                <Building2 className="h-3.5 w-3.5" />
                Business Call Handling
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">Announcement Control Plane</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
                Upload customer-owned closed-hours and override messages. Prompts are stored in the shared database so API containers and SIP engine runtimes resolve the same immutable audio source.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm text-slate-200 backdrop-blur">
              <div className="flex items-center gap-2 font-medium text-white">
                <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                Native playback ready
              </div>
              <p className="mt-1 text-xs text-slate-400">Raw G.711 uploads activate immediately; WAV/MP3 are normalized when ffmpeg is available.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)]">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-50 p-3 text-blue-700">
              <UploadCloud className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Upload Prompt</h2>
              <p className="text-sm text-slate-500">Create an active DB-backed announcement asset.</p>
            </div>
          </div>

          <div className="mt-6 space-y-5">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Customer</span>
              <select
                value={selectedCustomerId ?? ''}
                onChange={(event) => setSelectedCustomerId(event.target.value || undefined)}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                {customers.length === 0 ? <option value="">No customers available</option> : null}
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Display name</span>
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Closed for the evening"
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </label>

            <div>
              <span className="text-sm font-medium text-slate-700">Codec</span>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {(['PCMA', 'PCMU'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setCodec(option)}
                    className={`rounded-xl border px-4 py-3 text-left text-sm font-medium transition ${codec === option ? 'border-blue-600 bg-blue-50 text-blue-800 ring-2 ring-blue-500/20' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}
                  >
                    {option}
                    <span className="mt-1 block text-xs font-normal text-slate-500">Raw G.711, 8 kHz mono</span>
                  </button>
                ))}
              </div>
            </div>

            <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center hover:border-blue-400 hover:bg-blue-50/50">
              <FileAudio className="h-8 w-8 text-slate-400" />
              <span className="mt-3 text-sm font-medium text-slate-700">{selectedFile?.name ?? 'Choose .pcma/.pcmu/.wav/.mp3 audio'}</span>
              <span className="mt-1 text-xs text-slate-500">Maximum 5 MB. WAV/MP3 require ffmpeg on the API runtime.</span>
              <input
                type="file"
                accept=".pcma,.pcmu,.alaw,.ulaw,.mulaw,.wav,.mp3,audio/pcma,audio/pcmu,audio/alaw,audio/basic,audio/wav,audio/mpeg,audio/mp3"
                onChange={(event) => setSelectedFile(event.target.files?.[0])}
                className="sr-only"
              />
            </label>

            <button
              type="button"
              onClick={handleUpload}
              disabled={uploadPrompt.isPending || selectedCustomerId === undefined || selectedFile === undefined}
              className="w-full rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {uploadPrompt.isPending ? 'Uploading...' : 'Upload and Activate Prompt'}
            </button>

            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
              <div className="flex gap-2 font-semibold">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                Current format boundary
              </div>
              <p className="mt-1">Native announcement playback stores normalized G.711. WAV/MP3 uploads are transcoded server-side when ffmpeg is installed; otherwise upload raw PCMA/PCMU.</p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Prompt Inventory</h2>
                <p className="text-sm text-slate-500">{selectedCustomer?.name ?? 'Select a customer'} announcement assets.</p>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">{prompts.length} prompts</div>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {promptsQuery.isLoading ? (
              <div className="px-6 py-12 text-center text-sm text-slate-500">Loading prompts...</div>
            ) : prompts.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <FileAudio className="mx-auto h-10 w-10 text-slate-300" />
                <h3 className="mt-3 text-sm font-semibold text-slate-900">No prompts yet</h3>
                <p className="mt-1 text-sm text-slate-500">Upload a closed-hours or override message to make it available for policies.</p>
              </div>
            ) : prompts.map((prompt) => (
              <div key={prompt.id} className="px-6 py-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-sm font-semibold text-slate-950">{prompt.displayName}</h3>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium capitalize ring-1 ring-inset ${statusClass(prompt.status)}`}>{prompt.status}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span>{prompt.originalFilename}</span>
                      <span>{prompt.normalizedMediaType ?? prompt.originalMediaType}</span>
                      <span className="inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" />{formatDuration(prompt.durationMs)}</span>
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>v{prompt.version}</div>
                    <div>{new Date(prompt.createdAt).toLocaleString()}</div>
                  </div>
                </div>
                {prompt.status === 'active' && prompt.normalizedStorageKey !== null ? (
                  <div className="mt-4 rounded-xl bg-slate-50 px-3 py-2">
                    <audio
                      controls
                      preload="none"
                      src={apiAssetUrl(`/api/business/prompts/${prompt.id}/preview.wav`)}
                      className="h-9 w-full"
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.35fr)]">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-emerald-50 p-3 text-emerald-700">
              <Clock3 className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-950">{editingPolicyId === undefined ? 'Create Policy' : 'Edit Policy'}</h2>
              <p className="text-sm text-slate-500">Configure schedule, exceptions, and announcement behavior.</p>
            </div>
          </div>

          <div className="mt-6 space-y-5">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Policy name</span>
              <input
                value={policyName}
                onChange={(event) => setPolicyName(event.target.value)}
                placeholder="Main number business hours"
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </label>

            <div className="grid gap-3 sm:grid-cols-3">
              {(['sip_peer', 'peer_group', 'number'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setInboundResourceType(type)}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium ${inboundResourceType === type ? 'border-blue-600 bg-blue-50 text-blue-800 ring-2 ring-blue-500/20' : 'border-slate-200 bg-white text-slate-700'}`}
                >
                  {type.replace('_', ' ')}
                </button>
              ))}
            </div>

            {inboundResourceType === 'sip_peer' ? (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Inbound peer</span>
                <select value={inboundPeerId ?? ''} onChange={(event) => setInboundPeerId(event.target.value || undefined)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
                  {peers.map((peer) => <option key={peer.id} value={peer.id}>{peer.name}</option>)}
                </select>
              </label>
            ) : null}
            {inboundResourceType === 'peer_group' ? (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Inbound peer group</span>
                <select value={inboundPeerGroupId ?? ''} onChange={(event) => setInboundPeerGroupId(event.target.value || undefined)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
                  {peerGroups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                </select>
              </label>
            ) : null}
            {inboundResourceType === 'number' ? (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Inbound number</span>
                <input value={inboundNumber} onChange={(event) => setInboundNumber(event.target.value)} placeholder="+468280000" className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
              </label>
            ) : null}

            <div>
              <span className="text-sm font-medium text-slate-700">Open-hours action</span>
              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                {(['route_peer', 'route_peer_group', 'route_registered_aor'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setOpenActionType(type)}
                    className={`rounded-xl border px-3 py-2 text-sm font-medium ${openActionType === type ? 'border-emerald-600 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-500/20' : 'border-slate-200 bg-white text-slate-700'}`}
                  >
                    {type.replace('route_', '').replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>

            {openActionType === 'route_peer' ? (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Open destination peer</span>
                <select value={openPeerId ?? ''} onChange={(event) => setOpenPeerId(event.target.value || undefined)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
                  {peers.map((peer) => <option key={peer.id} value={peer.id}>{peer.name}</option>)}
                </select>
              </label>
            ) : null}
            {openActionType === 'route_peer_group' ? (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Open destination group</span>
                <select value={openPeerGroupId ?? ''} onChange={(event) => setOpenPeerGroupId(event.target.value || undefined)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
                  {peerGroups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                </select>
              </label>
            ) : null}
            {openActionType === 'route_registered_aor' ? (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Open destination AOR</span>
                <input value={openAor} onChange={(event) => setOpenAor(event.target.value)} placeholder="sip:1001@example.com" className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
              </label>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <CalendarDays className="h-4 w-4 text-emerald-700" />
                  Weekly open hours
                </div>
                <button
                  type="button"
                  onClick={() => setWeeklyWindows((current) => [...current, { id: crypto.randomUUID(), days: [], from: '09:00', to: '17:00' }])}
                  className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                >
                  Add window
                </button>
              </div>
              <div className="mt-4 space-y-4">
                {weeklyWindows.map((window) => (
                  <div key={window.id} className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
                    <div className="flex flex-wrap gap-2">
                      {BUSINESS_DAYS.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleWeeklyWindowDay(window.id, day.value)}
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${window.days.includes(day.value) ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300' : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200'}`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 grid grid-cols-[1fr_1fr_auto] gap-3">
                      <input type="time" value={window.from} onChange={(event) => updateWeeklyWindow(window.id, { from: event.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                      <input type="time" value={window.to} onChange={(event) => updateWeeklyWindow(window.id, { to: event.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                      <button type="button" onClick={() => setWeeklyWindows((current) => current.filter((item) => item.id !== window.id))} className="rounded-lg px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50">Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-900">Date exceptions</div>
                <button
                  type="button"
                  onClick={() => setExceptions((current) => [...current, { id: crypto.randomUUID(), date: '', closed: true, from: '09:00', to: '17:00', promptId: closedPromptId ?? '', reason: '' }])}
                  className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                >
                  Add exception
                </button>
              </div>
              <div className="mt-4 space-y-4">
                {exceptions.length === 0 ? <div className="text-xs text-slate-500">No holidays or one-off exceptions configured.</div> : null}
                {exceptions.map((exception) => (
                  <div key={exception.id} className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
                    <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-center">
                      <input type="date" value={exception.date} onChange={(event) => updateException(exception.id, { date: event.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={exception.closed} onChange={(event) => updateException(exception.id, { closed: event.target.checked })} />
                        Closed
                      </label>
                      <button type="button" onClick={() => setExceptions((current) => current.filter((item) => item.id !== exception.id))} className="rounded-lg px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50">Remove</button>
                    </div>
                    {!exception.closed ? (
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <input type="time" value={exception.from} onChange={(event) => updateException(exception.id, { from: event.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                        <input type="time" value={exception.to} onChange={(event) => updateException(exception.id, { to: event.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                      </div>
                    ) : null}
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <select value={exception.promptId} onChange={(event) => updateException(exception.id, { promptId: event.target.value })} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                        <option value="">Use policy default prompt</option>
                        {activePrompts.map((prompt) => <option key={prompt.id} value={prompt.id}>{prompt.displayName}</option>)}
                      </select>
                      <input value={exception.reason} onChange={(event) => updateException(exception.id, { reason: event.target.value })} placeholder="Reason shown in audit context" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Closed-hours prompt</span>
              <select value={closedPromptId ?? ''} onChange={(event) => setClosedPromptId(event.target.value || undefined)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
                {activePrompts.map((prompt) => <option key={prompt.id} value={prompt.id}>{prompt.displayName}</option>)}
              </select>
            </label>

            <button
              type="button"
              onClick={handleSavePolicy}
              disabled={createPolicy.isPending || updatePolicy.isPending || selectedCustomerId === undefined || activePrompts.length === 0}
              className="w-full rounded-xl bg-emerald-700 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {createPolicy.isPending || updatePolicy.isPending ? 'Saving...' : editingPolicyId === undefined ? 'Create Business-Hours Policy' : 'Update Business-Hours Policy'}
            </button>
            {editingPolicyId !== undefined ? (
              <button type="button" onClick={resetPolicyForm} className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Cancel Editing
              </button>
            ) : null}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Policies and Overrides</h2>
                <p className="text-sm text-slate-500">Enable manual response messages without changing schedules.</p>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">{policies.length} policies</div>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {policiesQuery.isLoading ? (
              <div className="px-6 py-12 text-center text-sm text-slate-500">Loading policies...</div>
            ) : policies.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-slate-500">No business call-handling policies yet.</div>
            ) : policies.map((policy) => (
              <div key={policy.id} className="px-6 py-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-950">{policy.name}</h3>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${policy.overrideState.enabled ? 'bg-rose-50 text-rose-700 ring-rose-600/20' : 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'}`}>
                        {policy.overrideState.enabled ? 'Override active' : 'Schedule active'}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      {policy.inboundResourceType.replace('_', ' ')} {'->'} {policy.openAction.type.replace('route_', '').replace(/_/g, ' ')} during open hours, announcement when closed.
                    </p>
                    {policy.overrideState.reason ? <p className="mt-1 text-xs text-rose-600">Reason: {policy.overrideState.reason}</p> : null}
                  </div>
                  {policy.overrideState.enabled ? (
                    <button
                      type="button"
                      onClick={() => void handleDisableOverride(policy.id)}
                      disabled={disableOverride.isPending}
                      className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                    >
                      Disable Override
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => loadPolicyIntoForm(policy)}
                    className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Edit Policy
                  </button>
                </div>

                {!policy.overrideState.enabled ? (
                  <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
                    <label className="block">
                      <span className="text-xs font-medium text-slate-700">Override prompt</span>
                      <select
                        value={overridePromptByPolicy[policy.id] ?? activePrompts[0]?.id ?? ''}
                        onChange={(event) => setOverridePromptByPolicy((current) => ({ ...current, [policy.id]: event.target.value }))}
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
                      >
                        {activePrompts.map((prompt) => <option key={prompt.id} value={prompt.id}>{prompt.displayName}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-slate-700">Reason</span>
                      <input
                        value={overrideReasonByPolicy[policy.id] ?? ''}
                        onChange={(event) => setOverrideReasonByPolicy((current) => ({ ...current, [policy.id]: event.target.value }))}
                        placeholder="Temporary closure"
                        className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => void handleEnableOverride(policy.id)}
                      disabled={enableOverride.isPending || activePrompts.length === 0}
                      className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:bg-slate-300"
                    >
                      Enable Override
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-indigo-50 p-3 text-indigo-700">
              <Tag className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Prompt Assignments</h2>
              <p className="text-sm text-slate-500">Map prompts to holidays, exceptions, temporary closures, and override contexts.</p>
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-600 ring-1 ring-slate-200">
            Assignment keys are stable labels such as <span className="font-mono">2026-12-24</span>, <span className="font-mono">holiday:christmas</span>, or <span className="font-mono">market-disruption</span>.
          </div>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.7fr)_minmax(0,1fr)_auto] lg:items-end">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Policy</span>
            <select value={selectedPolicyId ?? ''} onChange={(event) => setSelectedPolicyId(event.target.value || undefined)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
              {policies.map((policy) => <option key={policy.id} value={policy.id}>{policy.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Type</span>
            <select value={assignmentType} onChange={(event) => setAssignmentType(event.target.value as AssignmentType)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
              {ASSIGNMENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Prompt</span>
            <select value={assignmentPromptId ?? ''} onChange={(event) => setAssignmentPromptId(event.target.value || undefined)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
              {activePrompts.map((prompt) => <option key={prompt.id} value={prompt.id}>{prompt.displayName}</option>)}
            </select>
          </label>
          <button
            type="button"
            onClick={handleAssignPrompt}
            disabled={assignPrompt.isPending || selectedPolicyId === undefined || assignmentPromptId === undefined}
            className="rounded-xl bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-800 disabled:bg-slate-300"
          >
            Save Assignment
          </button>
        </div>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-700">Assignment key</span>
          <input value={assignmentKey} onChange={(event) => setAssignmentKey(event.target.value)} placeholder="Optional: 2026-12-24, holiday:christmas, market-disruption" className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
        </label>

        <div className="mt-6 divide-y divide-slate-100 rounded-2xl border border-slate-200">
          {selectedPolicyId === undefined ? (
            <div className="px-4 py-6 text-sm text-slate-500">Create or select a policy to manage assignments.</div>
          ) : assignments.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500">No prompt assignments for the selected policy.</div>
          ) : assignments.map((assignment) => {
            const prompt = prompts.find((candidate) => candidate.id === assignment.promptId);
            return (
              <div key={assignment.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{ASSIGNMENT_TYPES.find((type) => type.value === assignment.assignmentType)?.label ?? assignment.assignmentType}</div>
                  <div className="text-xs text-slate-500">{assignment.assignmentKey ?? 'No assignment key'} {'->'} {prompt?.displayName ?? assignment.promptId}</div>
                </div>
                <div className="text-xs text-slate-400">{new Date(assignment.createdAt).toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
